import { RenderPass } from "@/core/pipeline/render-pass"
import { Texture } from "@/core/backends/webgl/texture"
import { clamp } from "@/utils/math"

export interface DenoiseTextures {
	denoiseLumTexture: Texture
	denoiseColorTexture: Texture
}

export interface WaveletTextures {
	lumLevels: Texture[]
	singleChannelTexture: Texture
	tempW2H2: Texture
}

export interface waveletDenoiseOption {
	iso: number
	shutterSpeed: number
	isJpeg: boolean
}

export interface waveletDenoisePassOption extends waveletDenoiseOption {
	denoiseTextures: DenoiseTextures
	waveletTextures: WaveletTextures
}

interface waveletComputeOption {
	channel: number
	preSigma: number
	preThreshold: number
	preStrength: number
	waveletStrength: number
	maxLevel: number
	waveletLevelTextures: Texture[]
	tempW2H2: Texture
}

interface waveletShaderOption {
	step: number
	strength: number
	isVertical: boolean
	inWidth: number
	inHeight: number
	outWidth: number
	outHeight: number
}

export class WaveletDenoisePass extends RenderPass {
	public render(inputTexture: Texture, waveletPassOption: waveletDenoisePassOption) {
		const { denoiseTextures, waveletTextures, iso, shutterSpeed, isJpeg } = waveletPassOption
		const { denoiseLumTexture, denoiseColorTexture } = denoiseTextures

		// Determine intertal denoise strength parameters depending on ISO and shutterspeed
		const shutterNoise = iso > 800 ? (1 / shutterSpeed / 100) * 1.5 - 3 : 0
		const highISOExtra = iso > 7000 ? (iso - 6000) / 1200 + 2 : 0
		const noise = iso / 1000 + Math.max(0, shutterNoise) + highISOExtra
		const preSigma = noise > 5.5 && iso > 1900 ? 3 : 2
		const delta = iso > 1900 ? 0.01 : 0.003
		const preThreshold = iso <= 800 ? 0 : Math.min(noise * 0.0035 + delta, 0.06) //0.04
		const preStrength = clamp(noise / 7, 0.5, 1)
		const postThreshold = clamp(0.025 + highISOExtra * 0.005, 0.025, 0.055)

		let maxLevel = 4
		if ((iso > 2300 && noise > 5.5) || (iso > 3000 && noise > 3)) maxLevel = 6
		else if (iso >= 500) maxLevel = 5
		let maxLevelColor = maxLevel - 1
		if (iso > 6000) maxLevelColor = maxLevel

		let strengthScale = 1 + clamp(highISOExtra / 4, 0, 3)
		if (strengthScale > 2.5) strengthScale = 2.5 + (strengthScale - 2.5) * 0.5
		const waveletStrength =
			clamp(0.001 + iso * 0.000006, 0.0015, 0.001 * maxLevel) * strengthScale
		const waveletStrengthColor =
			Math.min(waveletStrength, 0.001 * maxLevelColor * 0.8) * strengthScale

		// Wavelet denoise for luminance channel (Y in YUV)
		const luminance = waveletTextures.singleChannelTexture
		luminance.updateSize(denoiseLumTexture.width, denoiseLumTexture.height)

		let channel = 0
		this.waveletDenoiseSingleChannel(inputTexture, luminance, {
			channel,
			preSigma,
			preThreshold,
			preStrength,
			waveletStrength,
			maxLevel,
			waveletLevelTextures: waveletTextures.lumLevels,
			tempW2H2: waveletTextures.tempW2H2,
		})

		// Do sharpen on luminance channel only to save memory (so luminance could be singleChannel format r16float)
		this.postprocess(luminance, denoiseLumTexture, postThreshold) // bilateral
		this.sharpen(denoiseLumTexture, luminance)
		this.boxBlur(luminance, denoiseLumTexture)

		// Color denoise: use wavelet for half size
		const colorChannelUV = waveletTextures.lumLevels[0] as Texture // size = W/2xH/2, use as temp buffer
		const colorChannelV = waveletTextures.lumLevels[1] as Texture
		colorChannelUV.updateSize(denoiseColorTexture.width, denoiseColorTexture.height)
		colorChannelV.updateSize(denoiseColorTexture.width, denoiseColorTexture.height)

		// Wavelet denoise for color channels
		channel = 1
		this.waveletDenoiseColorChannel(
			inputTexture,
			denoiseColorTexture, // temp texture to store U channel
			{
				channel,
				preSigma,
				preThreshold: isJpeg || iso <= 1200 ? 0 : preThreshold,
				preStrength,
				waveletStrength: waveletStrengthColor,
				maxLevel,
				waveletLevelTextures: waveletTextures.lumLevels,
				tempW2H2: waveletTextures.tempW2H2,
			},
		)

		channel = 2
		this.waveletDenoiseColorChannel(
			inputTexture,
			colorChannelV, // temp texture to store V channel
			{
				channel,
				preSigma,
				preThreshold: isJpeg || iso <= 1200 ? 0 : preThreshold,
				preStrength,
				waveletStrength: waveletStrengthColor,
				maxLevel,
				waveletLevelTextures: waveletTextures.lumLevels,
				tempW2H2: waveletTextures.tempW2H2,
			},
		)
		this.combine(denoiseColorTexture, colorChannelV, colorChannelUV, channel - 1)
		this.combine(colorChannelUV, colorChannelV, denoiseColorTexture, -1)
	}

	private waveletDenoiseSingleChannel(
		inputTexture: Texture,
		singleChannelMap: Texture,
		options: waveletComputeOption,
	) {
		const width = singleChannelMap.width
		const height = singleChannelMap.height
		const height2 = Math.floor((height + 1) / 2)
		const width2 = Math.floor((width + 1) / 2)

		const tempW2H2 = options.tempW2H2
		// const tempH2 = options.tempH2
		const waveletLevelTextures = options.waveletLevelTextures
		const maxLevel = Math.min(options.maxLevel, waveletLevelTextures.length)

		const texture0 = waveletLevelTextures[0] as Texture

		if (maxLevel <= 0) return

		for (let i = 0; i < maxLevel; i++) {
			waveletLevelTextures[i]?.updateSize(width2, height2)
		}

		tempW2H2.updateSize(width2, height2)
		//tempH2.updateSize(width, height2)
		const tempH2 = tempW2H2

		// optionally apply a bilateral filter, then proRGB->YUV and store channel component as r
		this.preprocess(inputTexture, singleChannelMap, options)

		// Decompose: level1 uses subsample, level2-4 use haar
		this.decomposeSubsample(singleChannelMap, tempH2, true, options.waveletStrength)
		this.decomposeSubsample(tempH2, texture0, false, options.waveletStrength)

		let step = 1
		for (let i = 1; i < maxLevel; i++) {
			const strength = (options.waveletStrength * (maxLevel - i)) / maxLevel
			this.decomposeHaar(
				waveletLevelTextures[i - 1] as Texture,
				waveletLevelTextures[i] as Texture,
				step,
				strength,
			)
			step *= 2
		}

		// Thresholding: already done in decompose shader

		// Compose
		for (let i = maxLevel - 1; i > 0; i--) {
			step /= 2
			this.composeHaar(
				waveletLevelTextures[i] as Texture,
				waveletLevelTextures[i - 1] as Texture,
				tempW2H2,
				step,
			)
			waveletLevelTextures[i - 1]?.swapWith(tempW2H2)
		}

		this.composeSubsample(waveletLevelTextures[0] as Texture, tempH2, false)
		this.composeSubsample(tempH2, singleChannelMap, true)

		return singleChannelMap
	}

	// Same flow as lum channel, but has some trick to use the larger wavelet textures of lum channel to save memory
	private waveletDenoiseColorChannel(
		inputTexture: Texture,
		singleChannelMap: Texture,
		options: waveletComputeOption,
	) {
		const width = singleChannelMap.width
		const height = singleChannelMap.height
		const height2 = Math.floor((height + 1) / 2)
		const width2 = Math.floor((width + 1) / 2)

		const tempW2H2 = options.tempW2H2
		const waveletLevelTextures = options.waveletLevelTextures
		const maxLevel = Math.min(options.maxLevel, waveletLevelTextures.length)

		if (maxLevel <= 0) return

		const texture0 = waveletLevelTextures[0] as Texture

		for (let i = 0; i < maxLevel; i++) {
			waveletLevelTextures[i]?.updateSize(width, height) // to avoid actually resize lum textures
		}

		//tempW2H2.updateSize(width2, height2)
		tempW2H2.updateSize(width, height) // to avoid actually resize lum textures
		const tempH2 = tempW2H2

		// optionally apply a bilateral filter, then proRGB->YUV and store channel component as r
		this.preprocess(inputTexture, singleChannelMap, options)

		// Decompose: level1 uses subsample, level2-4 use haar
		this.decomposeSubsampleColor(singleChannelMap, tempH2, {
			step: 1,
			strength: options.waveletStrength,
			isVertical: true,
			inWidth: width,
			inHeight: height,
			outWidth: width,
			outHeight: height2,
		})
		this.decomposeSubsampleColor(tempH2, texture0, {
			step: 1,
			strength: options.waveletStrength,
			isVertical: false,
			inWidth: width,
			inHeight: height2,
			outWidth: width2,
			outHeight: height2,
		})

		const waveletOption = {
			step: 1,
			strength: options.waveletStrength,
			isVertical: true,
			inWidth: width2,
			inHeight: height2,
			outWidth: width2,
			outHeight: height2,
		}
		let step = 1
		for (let i = 1; i < maxLevel; i++) {
			const strength = (options.waveletStrength * (maxLevel - i)) / maxLevel
			this.decomposeHaarColor(
				waveletLevelTextures[i - 1] as Texture,
				waveletLevelTextures[i] as Texture,
				{
					...waveletOption,
					step,
					strength,
				},
			)
			step *= 2
		}

		// Thresholding: already done in decompose shader

		// Compose
		for (let i = maxLevel - 1; i > 0; i--) {
			step /= 2
			this.composeHaarColor(
				waveletLevelTextures[i] as Texture,
				waveletLevelTextures[i - 1] as Texture,
				tempW2H2,
				{ ...waveletOption, step },
			)
			waveletLevelTextures[i - 1]?.swapWith(tempW2H2)
		}

		this.composeSubsampleColor(texture0, tempH2, {
			step: 1,
			strength: options.waveletStrength,
			isVertical: false,
			inWidth: width2,
			inHeight: height2,
			outWidth: width,
			outHeight: height2,
		})
		this.composeSubsampleColor(tempH2, singleChannelMap, {
			step: 1,
			strength: options.waveletStrength,
			isVertical: true,
			inWidth: width,
			inHeight: height2,
			outWidth: width,
			outHeight: height,
		})

		return singleChannelMap
	}

	private decomposeSubsample(
		inputTexture: Texture,
		out: Texture,
		isVertical: boolean,
		strength: number,
	) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.waveletDecomposeSubsample.render({
			inputTexture: 0,
			isVertical: isVertical,
			strength: strength,
		})

		return out
	}

	private decomposeHaar(inputTexture: Texture, out: Texture, step: number, strength: number) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.waveletDecomposeHaar.render({
			inputTexture: 0,
			step: step,
			strength: strength,
		})

		return out
	}

	private composeSubsample(inputTexture: Texture, out: Texture, isVertical: boolean) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.waveletComposeSubsample.render({
			inputTexture: 0,
			isVertical: isVertical,
		})

		return out
	}

	private composeHaar(
		inputTexture: Texture,
		originalOutput: Texture,
		out: Texture,
		step: number,
	) {
		out.bindFramebuffer()

		inputTexture.bind(0)
		originalOutput.bind(1)

		this.renderer.shaders.waveletComposeHaar.render({
			inputTexture: 0,
			originalOutput: 1,
			step: step,
		})

		return out
	}

	private decomposeSubsampleColor(
		inputTexture: Texture,
		out: Texture,
		option: waveletShaderOption,
	) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.waveletDecomposeSubsampleColor.render({
			inputTexture: 0,
			isVertical: option.isVertical,
			strength: option.strength,
			inWidth: option.inWidth,
			inHeight: option.inHeight,
			outWidth: option.outWidth,
			outHeight: option.outHeight,
		})

		return out
	}

	private decomposeHaarColor(inputTexture: Texture, out: Texture, option: waveletShaderOption) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.waveletDecomposeHaarColor.render({
			inputTexture: 0,
			step: option.step,
			strength: option.strength,
			width: option.inWidth,
			height: option.inHeight,
		})

		return out
	}

	private composeSubsampleColor(
		inputTexture: Texture,
		out: Texture,
		option: waveletShaderOption,
	) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.waveletComposeSubsampleColor.render({
			inputTexture: 0,
			isVertical: option.isVertical,
			inWidth: option.inWidth,
			inHeight: option.inHeight,
			outWidth: option.outWidth,
			outHeight: option.outHeight,
		})

		return out
	}

	private composeHaarColor(
		inputTexture: Texture,
		originalOutput: Texture,
		out: Texture,
		option: waveletShaderOption,
	) {
		out.bindFramebuffer()

		inputTexture.bind(0)
		originalOutput.bind(1)

		this.renderer.shaders.waveletComposeHaarColor.render({
			inputTexture: 0,
			originalOutput: 1,
			step: option.step,
			width: option.inWidth,
			height: option.inHeight,
		})

		return out
	}

	private preprocess(inputTexture: Texture, out: Texture, options: waveletComputeOption) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.waveletPreprocess.render({
			inputTexture: 0,
			channel: options.channel,
			sigma: options.preSigma,
			threshold: options.preThreshold,
			strength: options.preStrength,
		})

		return out
	}

	private postprocess(inputLum: Texture, out: Texture, threshold: number) {
		out.bindFramebuffer()

		inputLum.bind(0)

		this.renderer.shaders.waveletPostprocess.render({
			inputLum: 0,
			threshold: threshold,
		})

		return out
	}

	private sharpen(denoisedTexture: Texture, out: Texture) {
		out.bindFramebuffer()

		denoisedTexture.bind(0)

		this.renderer.shaders.waveletSharpen.render({
			denoisedTexture: 0,
		})

		return out
	}

	private boxBlur(inputTexture: Texture, out: Texture) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		this.renderer.shaders.boxBlur.render({
			inputTexture: 0,
		})

		return out
	}

	private combine(
		inputTexture: Texture,
		inputChannel: Texture,
		out: Texture,
		channelToCombine: number,
	) {
		out.bindFramebuffer()

		inputTexture.bind(0)
		inputChannel.bind(1)

		this.renderer.shaders.waveletCombine.render({
			inputTexture: 0,
			inputChannel: 1,
			channelToCombine: channelToCombine,
		})

		return out
	}

	dispose() {
		return
	}
}
